import { CollectionPaths } from "@order-up/common";
import { Counter, ENTITY_ID_RANGES, EntityType, ICounter, ICounterData, isPlatformEntity, type PlatformEntityType, Location as LocationRecord, type LocationEntityType, REVERSE_LEGACY_MAP, Tenant } from "@order-up/models";
import {
  CounterSchema,
  CreateCounterSchema,
  EntityTypeSchema,
  UpdateCounterSchema,
} from "@order-up/schemas";
import { v4 as uuidv4 } from "uuid";
import {
  DocumentWithId,
  FirestoreRepository,
} from "../interfaces/FirestoreRepository";
import { createLogger } from "../utils/logger";
import { resolveDocIds, resolveLocationDocId, resolveTenantDocId } from "./resolveDocIds";

const logger = createLogger("CounterService");

/**
 * Counter Service
 *
 * Provides consistent and unique ID generation across all entities using proper
 * schema validation and model instances. This service handles both:
 *
 * 1. Platform-level entities (globally unique): tenants, restaurants
 * 2. Restaurant-level entities (unique within restaurant): items, employees, etc.
 *
 * SCHEMA USAGE:
 * - CounterSchema: Validates complete counter data structure
 * - CreateCounterSchema: Validates data for creating new counters (omits timestamps)
 * - UpdateCounterSchema: Validates data for updating existing counters
 * - EntityTypeSchema: Validates entity type enum values
 *
 * MODEL USAGE:
 * - Counter: Model class with proper timestamp conversion from Firestore
 * - ICounter: Interface for complete counter data (readonly, includes timestamps)
 * - ICounterData: Interface for input data (mutable, optional timestamps)
 * - EntityType: Union type for all supported entity types
 *
 * All counter operations use schema validation before database operations
 * and return properly typed model instances with DocumentWithId<Counter>.
 *
 * USAGE EXAMPLE:
 * ```typescript
 * // Schema validation happens automatically:
 * const { tenantId, guid } = await counterService.generateTenantId();
 *
 * // Manual validation using interfaces:
 * const counterData: ICounterData = {
 *   entityType: 'items',
 *   nextId: 1
 * };
 * const isValid = counterService.validateCounterData(counterData);
 *
 * // Model creation with ICounterData:
 * const counter = counterService.createCounterModel({
 *   entityType: 'items',
 *   nextId: 1,
 *   createdAt: new Date(),
 *   updatedAt: new Date()
 * }); // Returns Counter instance
 *
 * // All functions return DocumentWithId<Counter> for type safety
 * const result: DocumentWithId<Counter> | null = await counterService.getCounterByEntityType(1, 1, 'items');
 * ```
 */
/**
 * Resolves the collection an entity's numeric ids live in, for the collision
 * probe in generateNextId. `null` means "no queryable collection of its own" —
 * see NO_COLLISION_PROBE.
 */
type EntityCollectionPathResolver = (
  tenantDocId: string,
  locationDocId: string,
) => string;

/**
 * The entity's ids are NOT a queryable top-level field on a collection of its
 * own, so there is nothing to probe for collisions. The transactional counter is
 * the sole authority and drift reconciliation is skipped.
 *
 * `tables` live as array elements inside a `table_layouts` document, so
 * `where('tableId','==',n)` would match nothing and the probe would be a wasted
 * read inside the allocation transaction.
 *
 * RTDB-minted entities are also listed, but they never reach the map — see the
 * refusal in generateNextId.
 */
const NO_COLLISION_PROBE = null;

/**
 * Entities whose ids come from the shared RTDB counter, never from a Firestore
 * counter document. The client package mints NONE of them: the server's
 * checked allocator (`counterService.generateOrderId` / `generateCheckId` in
 * @order-up/core) and the POS's RTDB transaction do. Routing one through
 * `generateNextId` would mint from the WRONG counter and duplicate a live
 * order/check number — which is also why this package's own `generateOrderId`
 * (a read-then-write on a Firestore `counters/orders` document, with no
 * transaction and no caller) was deleted.
 *
 * Mirrors RTDB_COUNTER_ENTITIES in packages/core/src/services/counterService.ts,
 * which returns early for the same set. Here there is no Firestore-side
 * equivalent to fall back to, so the call is refused outright rather than
 * silently served.
 */
const RTDB_MINTED_ENTITIES: ReadonlySet<EntityType> = new Set<EntityType>([
  "orders",
  "checks",
  "time_entries",
  "transfer_records",
]);

/**
 * Map entity types to CollectionPaths functions (restaurant-level entities only).
 *
 * TOTAL BY TYPE. `Record<RestaurantEntityType, …>` means `tsc` fails the build
 * when an EntityType is added to ENTITY_ID_RANGES without landing here. That is
 * the guard — do not weaken it to a partial type and do not re-add `as const`,
 * which would infer a narrower key set and silently restore the gap.
 *
 * This map was missing `tables` and `shift_tasks`, so
 * `counterService.generateTableId` (tableLayouts.ts:216, reached from
 * table-service/layouts) and `generateShiftTaskId` (shiftTasks.ts:82) each
 * evaluated `undefined(...)` → TypeError → CounterError → 500. Adding a new
 * table to a layout, and creating a shift task, both failed in production.
 */
const COLLECTION_PATH_MAP: Record<
  LocationEntityType,
  EntityCollectionPathResolver | null
> = {
  items: CollectionPaths.items,
  courses: CollectionPaths.courses,
  sales_categories: CollectionPaths.salesCategories,
  tax_rates: CollectionPaths.taxRates,
  modifier_groups: CollectionPaths.modifierGroups,
  modifiers: CollectionPaths.modifiers,
  menu_groups: CollectionPaths.menuGroups,
  tags: CollectionPaths.tags,
  prep_stations: CollectionPaths.prepStations,
  printers: CollectionPaths.printers,
  employees: CollectionPaths.employees,
  job_titles: CollectionPaths.jobTitles,
  departments: CollectionPaths.departments,
  shifts: CollectionPaths.shifts,
  reviews: CollectionPaths.reviews,
  media: CollectionPaths.media,
  table_layouts: CollectionPaths.tableLayouts,
  revenue_centers: CollectionPaths.revenueCenters,
  table_assignments: CollectionPaths.tableAssignments,
  table_merges: CollectionPaths.tableMerges,
  waitlist_entries: CollectionPaths.waitlistEntries,
  seated_customers: CollectionPaths.seatedCustomers,
  reservations: CollectionPaths.reservations,
  devices: CollectionPaths.devices,
  notifications: CollectionPaths.notifications,
  job_permissions: CollectionPaths.jobPermissions,
  discounts: CollectionPaths.discounts,
  void_reasons: CollectionPaths.voidReasons,
  payments: CollectionPaths.payments,
  // Present in core's map but never mirrored here. `shift_tasks` was reachable
  // (shiftTasks.ts:82) and 500ing; the rest were latent.
  shift_tasks: CollectionPaths.shiftTasks,
  cash_drawers: CollectionPaths.cashDrawers,
  kitchen_buzzers: CollectionPaths.kitchenBuzzers,
  schedules: CollectionPaths.schedules,
  shift_requests: CollectionPaths.shiftRequests,
  labor_warnings: CollectionPaths.laborWarnings,
  network_configs: CollectionPaths.networkConfigs,
  carts: CollectionPaths.carts,
  bookable_services: CollectionPaths.bookableServices,
  appointments: CollectionPaths.appointments,
  // Gift cards are TENANT-scoped: these path helpers take only tenantDocId and
  // ignore the restaurant argument, which is correct — a card is spendable at
  // every restaurant in the tenant.
  //
  // `gift_cards` is NOT probed: no client may read card documents (spec
  // 2026-10-08 §2.2 — the number is the money and has no PIN; firestore.rules
  // denies every client and device read), so a client probe would only ever
  // fail. Gift card ids are minted server-side (core `generateNextId`, inside
  // issueCard / the import commit), where the probe does not apply.
  gift_cards: NO_COLLISION_PROBE,
  gift_card_transactions: CollectionPaths.giftCardTransactions,
  // Ids live inside `table_layouts` documents as array elements — there is no
  // `tables` collection to probe. This entry is what unbreaks generateTableId.
  tables: NO_COLLISION_PROBE,
  // RTDB-minted; refused before this map is read. Entries exist only to keep the
  // Record total.
  orders: NO_COLLISION_PROBE,
  checks: NO_COLLISION_PROBE,
  time_entries: NO_COLLISION_PROBE,
  transfer_records: NO_COLLISION_PROBE,
};

/**
 * The entities deliberately allocated WITHOUT a collision probe.
 *
 * Exported purely so the completeness test can pin the exact set. The total
 * Record above forces every EntityType to have an entry — and when `tsc`
 * complains about a newly added one, `NO_COLLISION_PROBE` is the path of least
 * resistance. Taking it silently gives up drift reconciliation for that entity,
 * so widening this set has to be a deliberate, reviewed edit rather than the
 * quickest route to a green build.
 */
export const ENTITIES_WITHOUT_COLLISION_PROBE: ReadonlySet<LocationEntityType> =
  new Set(
    (Object.keys(COLLECTION_PATH_MAP) as LocationEntityType[]).filter(
      (entityType) => COLLECTION_PATH_MAP[entityType] === null,
    ),
  );

/**
 * Map platform entities to their collection paths
 */
const PLATFORM_COLLECTION_MAP = {
  tenants: CollectionPaths.tenants,
  restaurants: CollectionPaths.restaurants, // Note: This will need tenantDocId when validating
  domains: CollectionPaths.domains,
  integrations: CollectionPaths.integrations,
  system_roles: CollectionPaths.systemRoles,
  allergy_categories: CollectionPaths.allergyCategories,
  ingredients: CollectionPaths.ingredients,
  platform_requests: CollectionPaths.platformRequests,
  permission_groups: CollectionPaths.permissionGroups,
  permission_sub_accesses: CollectionPaths.permissionSubAccesses,
  support_sessions: CollectionPaths.supportSessions,
  business_types: CollectionPaths.businessTypes,
  business_sub_categories: CollectionPaths.businessSubCategories,
} as const;

/** Passthrough model class for polymorphic collision-detection queries */
const passthroughModel = {
  fromJSON: (data: Record<string, unknown>) => data as { [key: string]: unknown },
};

/**
 * Checks whether an entity with the given numeric business ID already exists
 * in the target collection. Used for defensive collision detection during
 * counter-based ID generation.
 *
 * The entity type varies at runtime across 37+ types, so this helper
 * encapsulates the polymorphic query and only returns a boolean.
 */
async function entityIdExists(
  collectionPath: string,
  idField: string,
  idValue: number,
): Promise<boolean> {
  const repo = new FirestoreRepository<{ [key: string]: unknown }>(collectionPath, passthroughModel);
  const results = await repo.find(
    [{ field: idField, operator: "==", value: idValue }],
    [],
    1,
  );
  return results.length > 0;
}

/**
 * Returns the smallest numeric business id that already exists in the target
 * collection within the inclusive range [startId, endId], or `null` if the
 * whole range is free. This is the range-reservation analogue of
 * {@link entityIdExists}: instead of one `==` probe per candidate id (which
 * would cost `count` reads), it uses a single ordered range query per attempt,
 * and returns the colliding id so the caller can jump the reservation window
 * past it. Used to find a clean contiguous starting point for bulk imports.
 */
async function firstExistingIdInRange(
  collectionPath: string,
  idField: string,
  startId: number,
  endId: number,
): Promise<number | null> {
  const repo = new FirestoreRepository<{ [key: string]: unknown }>(collectionPath, passthroughModel);
  const results = await repo.find(
    [
      { field: idField, operator: ">=", value: startId },
      { field: idField, operator: "<=", value: endId },
    ],
    [{ field: idField, direction: "asc" }],
    1,
  );
  if (results.length === 0) return null;
  const value = results[0][idField];
  return typeof value === "number" ? value : null;
}

export class CounterError extends Error {
  public readonly code: string;
  public readonly originalError?: unknown;

  constructor(message: string, code: string, originalError?: unknown) {
    super(message);
    this.name = "CounterError";
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Lazy-migrate a counter doc from legacy camelCase to snake_case.
 * Uses FirestoreRepository to read/write, matching the rest of this service.
 */
async function migrateCounterIfNeeded(
  collectionPath: string,
  entityType: string,
): Promise<void> {
  const legacyName = REVERSE_LEGACY_MAP[entityType];
  if (!legacyName) return;

  const repo = new FirestoreRepository<Counter>(collectionPath, Counter);

  // If the new snake_case doc already exists, nothing to do
  const existing = await repo.getById(entityType);
  if (existing) return;

  // Check if a legacy camelCase doc exists
  const legacy = await repo.getById(legacyName);
  if (!legacy) return;

  // Migrate: create under new name, delete old
  const data: Record<string, unknown> = {
    entityType,
    nextId: legacy.nextId,
    createdAt: FirestoreRepository.getServerTimestamp(),
    updatedAt: FirestoreRepository.getServerTimestamp(),
  };
  await repo.createWithId(entityType, data);
  await repo.deleteById(legacyName);
  logger.info("Migrated counter from legacy camelCase", { from: legacyName, to: entityType });
}

/**
 * Gets the current counter value for an entity
 */
const getCounterByEntityType = async (
  tenantId: number,
  locationNumericId: number,
  entityType: EntityType,
): Promise<DocumentWithId<Counter> | null> => {
  logger.debug("Getting counter for entity", {
    entityType,
    tenantId,
    restaurantId: locationNumericId,
  });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new CounterError(
        `Tenant not found with tenantId: ${tenantId}`,
        "TENANT_NOT_FOUND",
      );
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new CounterError(
        `Restaurant not found with restaurantId: ${locationNumericId}`,
        "RESTAURANT_NOT_FOUND",
      );
    }

    const collectionPath = CollectionPaths.counters(
      tenantDocId,
      locationDocId,
    );
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );
    await migrateCounterIfNeeded(collectionPath, entityType);
    const counter = await counterRepo.getById(entityType);

    logger.debug("Retrieved counter for entity", {
      entityType,
      nextId: counter?.nextId || "not found",
    });
    return counter;
  } catch (error) {
    logger.error("Error fetching counter", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Creates or updates a counter for an entity
 */
const upsertCounter = async (
  tenantId: number,
  locationNumericId: number,
  entityType: EntityType,
  nextId?: number,
): Promise<DocumentWithId<Counter>> => {
  logger.debug("Upserting counter", { entityType, nextId: nextId ?? "auto" });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new CounterError(
        `Tenant not found with tenantId: ${tenantId}`,
        "TENANT_NOT_FOUND",
      );
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new CounterError(
        `Restaurant not found with restaurantId: ${locationNumericId}`,
        "RESTAURANT_NOT_FOUND",
      );
    }

    const collectionPath = CollectionPaths.counters(
      tenantDocId,
      locationDocId,
    );
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );

    // Prepare counter data using ICounterData interface
    const counterData: ICounterData = {
      entityType,
      nextId: nextId ?? ENTITY_ID_RANGES[entityType].startId - 1,
    };

    // Validate the counter data
    const validatedData = CreateCounterSchema.parse(counterData);
    logger.debug("Counter data passed validation");

    // Create counter payload with server timestamps
    const counterPayload: any = {
      ...validatedData,
      createdAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    // Lazy-migrate legacy camelCase counter doc if needed
    await migrateCounterIfNeeded(collectionPath, entityType);

    // Check if counter exists
    const existingCounter = await counterRepo.getById(entityType);

    if (existingCounter) {
      // Prepare update data using Partial<ICounterData>
      const updateData: Partial<ICounterData> = {
        nextId: nextId ?? existingCounter.nextId,
      };
      const validatedUpdateData = UpdateCounterSchema.parse(updateData);
      logger.debug("Counter update data passed validation");

      // Update existing counter
      await counterRepo.updateById(entityType, {
        ...validatedUpdateData,
        updatedAt: FirestoreRepository.getServerTimestamp(),
      });

      const updatedCounter = await counterRepo.getById(entityType);
      if (!updatedCounter) {
        throw new CounterError(
          `Failed to retrieve updated counter for ${entityType}`,
          "UPDATE_FAILED",
        );
      }
      return updatedCounter;
    } else {
      // Create new counter
      await counterRepo.createWithId(entityType, counterPayload);
      const createdCounter = await counterRepo.getById(entityType);
      if (!createdCounter) {
        throw new CounterError(
          `Failed to retrieve created counter for ${entityType}`,
          "CREATE_FAILED",
        );
      }
      return createdCounter;
    }
  } catch (error) {
    logger.error("Error upserting counter", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Gets the current counter value for a platform-level entity
 */
const getPlatformCounterByEntityType = async (
  entityType: PlatformEntityType,
): Promise<DocumentWithId<Counter> | null> => {
  logger.debug("Getting platform counter", { entityType });

  try {
    const collectionPath = CollectionPaths.platformCounters();
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );
    const counter = await counterRepo.getById(entityType);

    logger.debug("Retrieved platform counter", {
      entityType,
      nextId: counter?.nextId ?? "not found",
    });
    return counter;
  } catch (error) {
    logger.error("Error fetching platform counter", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Creates or updates a platform-level counter
 */
const upsertPlatformCounter = async (
  entityType: PlatformEntityType,
  nextId?: number,
): Promise<DocumentWithId<Counter>> => {
  logger.debug("Upserting platform counter", {
    entityType,
    nextId: nextId ?? "auto",
  });

  try {
    const collectionPath = CollectionPaths.platformCounters();
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );

    // Prepare counter data using ICounterData interface
    const counterData: ICounterData = {
      entityType,
      nextId: nextId ?? ENTITY_ID_RANGES[entityType].startId - 1,
    };

    // Validate the counter data
    const validatedData = CreateCounterSchema.parse(counterData);
    logger.debug("Platform counter data passed validation");

    // Create counter payload with server timestamps
    const counterPayload: any = {
      ...validatedData,
      createdAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    // Check if counter exists
    const existingCounter = await counterRepo.getById(entityType);

    if (existingCounter) {
      // Prepare update data using Partial<ICounterData>
      const updateData: Partial<ICounterData> = {
        nextId: nextId ?? existingCounter.nextId,
      };
      const validatedUpdateData = UpdateCounterSchema.parse(updateData);
      logger.debug("Platform counter update data passed validation");

      // Update existing counter
      await counterRepo.updateById(entityType, {
        ...validatedUpdateData,
        updatedAt: FirestoreRepository.getServerTimestamp(),
      });

      const updatedCounter = await counterRepo.getById(entityType);
      if (!updatedCounter) {
        throw new CounterError(
          `Failed to retrieve updated platform counter for ${entityType}`,
          "UPDATE_FAILED",
        );
      }
      return updatedCounter;
    } else {
      // Create new counter
      await counterRepo.createWithId(entityType, counterPayload);
      const createdCounter = await counterRepo.getById(entityType);
      if (!createdCounter) {
        throw new CounterError(
          `Failed to retrieve created platform counter for ${entityType}`,
          "CREATE_FAILED",
        );
      }
      return createdCounter;
    }
  } catch (error) {
    logger.error("Error upserting platform counter", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Generates the next unique ID for a platform-level entity (tenant, restaurant, or domain mapping)
 */
const generatePlatformNextId = async (
  entityType: PlatformEntityType,
): Promise<{ entityId: number; guid: string }> => {
  logger.debug("Generating platform next ID", { entityType });

  try {
    const collectionPath = CollectionPaths.platformCounters();
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );

    // Lazy-migrate legacy camelCase counter doc if needed
    await migrateCounterIfNeeded(collectionPath, entityType);

    // Get current counter first
    let counter = await counterRepo.getById(entityType);

    if (!counter) {
      // Initialize counter if it doesn't exist
      counter = await upsertPlatformCounter(entityType);
    }

    let nextId = counter.nextId + 1;
    const entityConfig = ENTITY_ID_RANGES[entityType];

    // Handle collision detection based on entity type
    if (
      entityType === "tenants" ||
      entityType === "domains" ||
      entityType === "integrations" ||
      entityType === "system_roles" ||
      entityType === "allergy_categories" ||
      entityType === "ingredients" ||
      entityType === "platform_requests" ||
      entityType === "permission_groups" ||
      entityType === "permission_sub_accesses" ||
      entityType === "support_sessions" ||
      entityType === "business_types" ||
      entityType === "business_sub_categories"
    ) {
      // Use PLATFORM_COLLECTION_MAP for simple platform entity validation
      const entityCollectionPathFn = PLATFORM_COLLECTION_MAP[entityType];
      const entityCollectionPath = entityCollectionPathFn();

      // Auto-advance past any IDs that already exist (handles out-of-sync counters)
      const maxAttempts = 100;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (!(await entityIdExists(entityCollectionPath, entityConfig.idField, nextId))) {
          break;
        }
        if (attempt === maxAttempts - 1) {
          throw new CounterError(
            `Could not find a free ${entityConfig.idField} for ${entityType} after ${maxAttempts} attempts.`,
            "ID_COLLISION",
          );
        }
        logger.warn("Counter out of sync, advancing", { entityType, skippedId: nextId });
        nextId++;
      }
    } else if (entityType === "restaurants") { // scoped-path-ok: counter entity key (stored doc id)
      // For restaurants, we need to check across all tenants since restaurants are globally unique
      // First get all tenants, then check their restaurant subcollections
      const tenantRepo = new FirestoreRepository<Tenant>(
        CollectionPaths.tenants(),
        Tenant,
      );
      const tenantsResult = await tenantRepo.find([]);

      // Check each tenant's restaurants subcollection
      for (const tenant of tenantsResult) {
        const locationCollectionPath = CollectionPaths.restaurants(
          tenant.docId,
        );
        const locationRepo = new FirestoreRepository<LocationRecord>(
          locationCollectionPath,
          LocationRecord,
        );
        const existingLocation = await locationRepo.find(
          [{ field: entityConfig.idField, operator: "==", value: nextId }],
          [],
          1,
        );

        if (existingLocation.length > 0) {
          throw new CounterError(
            `${entityConfig.idField} ${nextId} already exists for ${entityType} in tenant ${tenant.docId}. Counter may be out of sync.`,
            "ID_COLLISION",
          );
        }
      }
      logger.debug("Validated restaurant ID across all tenants", { nextId });
    }

    // Update counter
    await counterRepo.updateById(entityType, {
      nextId,
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    // Generate GUID using uuid
    const guid = uuidv4();

    logger.debug("Generated platform ID and GUID", {
      entityType,
      nextId,
      guid,
    });
    return { entityId: nextId, guid };
  } catch (error) {
    logger.error("Error generating platform next ID", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Generates the next unique ID for an entity (handles both platform and restaurant-level)
 */
const generateNextId = async (
  entityType: EntityType,
  tenantId?: number,
  locationNumericId?: number,
): Promise<{ entityId: number; guid: string }> => {
  logger.debug("Generating next ID", { entityType });

  // Handle platform-level entities
  if (isPlatformEntity(entityType)) {
    return generatePlatformNextId(entityType);
  }

  // Refuse RTDB-minted entities outright. Serving them from the Firestore
  // counter below would mint a number the RTDB counter has already issued —
  // a duplicate live order/check number, silently. Callers want the server's
  // allocator (@order-up/core counterService.generateOrderId and friends).
  if (RTDB_MINTED_ENTITIES.has(entityType)) {
    throw new CounterError(
      `${entityType} ids are minted from the RTDB counter, not here — use the dedicated allocator`,
      "ENTITY_NOT_ALLOCATED_HERE",
    );
  }

  // Handle restaurant-level entities
  if (!tenantId || !locationNumericId) {
    throw new CounterError(
      `tenantId and restaurantId are required for restaurant-level entity: ${entityType}`,
      "MISSING_REQUIRED_PARAMS",
    );
  }

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new CounterError(
        `Tenant not found with tenantId: ${tenantId}`,
        "TENANT_NOT_FOUND",
      );
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new CounterError(
        `Restaurant not found with restaurantId: ${locationNumericId}`,
        "RESTAURANT_NOT_FOUND",
      );
    }

    const collectionPath = CollectionPaths.counters(
      tenantDocId,
      locationDocId,
    );
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );

    // Get the collection path for the entity type. `null` (NO_COLLISION_PROBE)
    // is a legitimate answer, not a missing entry — the map is a total Record,
    // so a genuinely absent key is a compile error, never an `undefined(...)`
    // call at runtime.
    const resolveEntityCollectionPath = COLLECTION_PATH_MAP[entityType];
    const entityCollectionPath = resolveEntityCollectionPath
      ? resolveEntityCollectionPath(tenantDocId, locationDocId)
      : null;

    // Lazy-migrate legacy camelCase counter doc if needed
    await migrateCounterIfNeeded(collectionPath, entityType);

    // Get current counter
    let counter = await counterRepo.getById(entityType);

    if (!counter) {
      // Initialize counter if it doesn't exist
      counter = await upsertCounter(tenantId, locationNumericId, entityType);
    }

    let nextId = counter.nextId + 1;
    const entityConfig = ENTITY_ID_RANGES[entityType];

    // Auto-advance past any IDs that already exist (handles out-of-sync counters).
    // Skipped entirely when the entity has no collection of its own
    // (NO_COLLISION_PROBE): there is nothing to query, so the counter is the
    // sole authority.
    if (entityCollectionPath !== null) {
      const maxAttempts = 100;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (!(await entityIdExists(entityCollectionPath, entityConfig.idField, nextId))) {
          break;
        }
        if (attempt === maxAttempts - 1) {
          throw new CounterError(
            `Could not find a free ${entityConfig.idField} for ${entityType} after ${maxAttempts} attempts.`,
            "ID_COLLISION",
          );
        }
        logger.warn("Counter out of sync, advancing", { entityType, skippedId: nextId });
        nextId++;
      }
    }

    // Update counter
    await counterRepo.updateById(entityType, {
      nextId,
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    // Generate GUID using uuid
    const guid = uuidv4();

    logger.debug("Generated ID and GUID", { entityType, nextId, guid });
    return { entityId: nextId, guid };
  } catch (error) {
    logger.error("Error generating next ID", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Reserve a CONTIGUOUS range of `count` numeric ids for a restaurant-level
 * entity in a single read-modify-write, and return one `{ entityId, guid }` per
 * reserved id (start..start+count-1, each with its own uuidv4 guid).
 *
 * WHY: bulk importers previously called the per-row single generator once per
 * row — each call resolving docIds, reading the counter, and writing it back
 * (~5-6 sequential Firestore round trips). A 200-row import ≈ 1,200 serial
 * round trips just for ids. This reserves the whole block at once:
 *   • resolve docIds ONCE (shared session-cached resolver),
 *   • read the counter ONCE,
 *   • advance `nextId` by `count` in ONE update.
 *
 * SEMANTICS: identical to `generateNextId` applied `count` times back-to-back,
 * with the same defensive out-of-sync-counter behaviour: the counter stores the
 * last-assigned id, so the block starts at `counter.nextId + 1`. It mirrors the
 * single generator's collision probe — but over the whole block via a single
 * range query per attempt (see {@link firstExistingIdInRange}) instead of one
 * `==` read per id — advancing the window past any pre-existing id. Gaps that
 * result from skipping occupied ids are exactly what the single path already
 * tolerates; no id is ever reused.
 *
 * Restaurant-level only (items / modifier_groups / menu_groups / employees, and
 * any other COLLECTION_PATH_MAP entity). Platform-level entities are globally
 * unique and must keep using the single generator.
 */
const generateNextIds = async (
  entityType: EntityType,
  tenantId: number,
  locationNumericId: number,
  count: number,
): Promise<{ entityId: number; guid: string }[]> => {
  logger.debug("Reserving ID range", { entityType, count });

  if (!Number.isInteger(count) || count < 1) {
    throw new CounterError(
      `count must be a positive integer, got: ${count}`,
      "INVALID_COUNT",
    );
  }

  if (isPlatformEntity(entityType)) {
    throw new CounterError(
      `Range reservation is not supported for platform-level entity: ${entityType}`,
      "UNSUPPORTED_ENTITY",
    );
  }

  try {
    // Resolve both docIds ONCE via the shared session-cached resolver.
    const { tenantDocId, restaurantDocId: locationDocId } = await resolveDocIds(
      tenantId,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.counters(
      tenantDocId,
      locationDocId,
    );
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );

    // Collection the ids belong to (for the defensive collision probe). `null`
    // (NO_COLLISION_PROBE) is a legitimate answer, not a missing entry — the map
    // is a total Record, so an absent key is a compile error rather than an
    // `undefined(...)` call here.
    const resolveEntityCollectionPath = COLLECTION_PATH_MAP[entityType];
    const entityCollectionPath = resolveEntityCollectionPath
      ? resolveEntityCollectionPath(tenantDocId, locationDocId)
      : null;

    // Lazy-migrate legacy camelCase counter doc if needed
    await migrateCounterIfNeeded(collectionPath, entityType);

    // Read the counter ONCE (initialize if absent).
    let counter = await counterRepo.getById(entityType);
    if (!counter) {
      counter = await upsertCounter(tenantId, locationNumericId, entityType);
    }

    const entityConfig = ENTITY_ID_RANGES[entityType];

    // Find a contiguous, collision-free start. The counter stores the last
    // assigned id, so the block runs [start, start + count - 1] beginning at
    // counter.nextId + 1. Advance past any pre-existing id (out-of-sync
    // counter) using a single range query per attempt.
    let start = counter.nextId + 1;
    const maxAttempts = 100;
    for (let attempt = 0; entityCollectionPath !== null && attempt < maxAttempts; attempt++) {
      const collidingId = await firstExistingIdInRange(
        entityCollectionPath,
        entityConfig.idField,
        start,
        start + count - 1,
      );
      if (collidingId === null) break;
      if (attempt === maxAttempts - 1) {
        throw new CounterError(
          `Could not find a free contiguous ${entityConfig.idField} range of ${count} for ${entityType} after ${maxAttempts} attempts.`,
          "ID_COLLISION",
        );
      }
      logger.warn("Counter out of sync, advancing reservation past collision", {
        entityType,
        attemptedStart: start,
        collidingId,
      });
      // Jump the whole window past the collision so the block stays contiguous.
      start = collidingId + 1;
    }

    const lastId = start + count - 1;

    // Advance the counter to the last reserved id in a SINGLE update.
    await counterRepo.updateById(entityType, {
      nextId: lastId,
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    const reserved: { entityId: number; guid: string }[] = [];
    for (let id = start; id <= lastId; id++) {
      reserved.push({ entityId: id, guid: uuidv4() });
    }

    logger.debug("Reserved contiguous ID range", {
      entityType,
      start,
      lastId,
      count,
    });
    return reserved;
  } catch (error) {
    logger.error("Error reserving ID range", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Validates entity type using schema
 */
const validateEntityType = (entityType: string): entityType is EntityType => {
  try {
    EntityTypeSchema.parse(entityType);
    return true;
  } catch (error) {
    logger.error("Invalid entity type", {
      entityType,
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Validates counter data using schema for input data
 */
const validateCounterData = (data: ICounterData): boolean => {
  try {
    CreateCounterSchema.parse(data);
    return true;
  } catch (error) {
    logger.error("Invalid counter data", {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Validates complete counter object using ICounter interface and schema
 */
const validateCounter = (data: ICounter): boolean => {
  try {
    CounterSchema.parse(data);
    return true;
  } catch (error) {
    logger.error("Invalid counter", {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

/**
 * Validates that an ID is in the correct range for an entity and returns detailed info
 */
const validateIdRange = (
  entityType: EntityType,
  entityId: number,
): { isValid: boolean; message?: string } => {
  // First validate the entity type
  if (!validateEntityType(entityType)) {
    return { isValid: false, message: `Invalid entity type: ${entityType}` };
  }

  const entityConfig = ENTITY_ID_RANGES[entityType];
  const isValid = entityId >= entityConfig.startId;

  if (!isValid) {
    return {
      isValid: false,
      message: `ID ${entityId} is below minimum ${entityConfig.startId} for entity type ${entityType}`,
    };
  }

  return { isValid: true };
};

/**
 * Converts a Counter model instance to ICounter interface object
 */
const counterToInterface = (counter: Counter): ICounter => {
  return counter.toJSON() as ICounter;
};

/**
 * Creates a Counter model instance from validated ICounterData
 */
const createCounterModel = (data: ICounterData): Counter => {
  // Validate the data first using our validation function
  if (!validateCounterData(data)) {
    throw new Error("Invalid counter data provided to createCounterModel");
  }
  return new Counter(data);
};

/**
 * Generate a suggested PLU code (user can override)
 */
const generateSuggestedPLU = (
  entityType: EntityType,
  entityId: number,
  name?: string,
): string => {
  const entityConfig = ENTITY_ID_RANGES[entityType];
  const nameSlug = name
    ? name
        .replace(/[^a-zA-Z0-9]/g, "")
        .substring(0, 6)
        .toUpperCase()
    : "";
  return `${entityConfig.prefix}${entityId}${nameSlug ? "_" + nameSlug : ""}`;
};

/**
 * Resets a counter for an entity (admin function - use with caution)
 */
const resetCounter = async (
  tenantId: number,
  locationNumericId: number,
  entityType: EntityType,
  newValue?: number,
): Promise<void> => {
  logger.debug("Resetting counter", {
    entityType,
    newValue: newValue ?? "default",
  });

  const resetValue = newValue ?? ENTITY_ID_RANGES[entityType].startId - 1;
  await upsertCounter(tenantId, locationNumericId, entityType, resetValue);

  logger.debug("Successfully reset counter", { entityType, resetValue });
};

/**
 * Gets all counters for a restaurant
 */
const getAllCounters = async (
  tenantId: number,
  locationNumericId: number,
): Promise<DocumentWithId<Counter>[]> => {
  logger.debug("Getting all counters", { tenantId, restaurantId: locationNumericId });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new CounterError(
        `Tenant not found with tenantId: ${tenantId}`,
        "TENANT_NOT_FOUND",
      );
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new CounterError(
        `Restaurant not found with restaurantId: ${locationNumericId}`,
        "RESTAURANT_NOT_FOUND",
      );
    }

    const collectionPath = CollectionPaths.counters(
      tenantDocId,
      locationDocId,
    );
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );
    const counters = await counterRepo.find();

    logger.debug("Retrieved all counters", {
      restaurantId: locationNumericId,
      counterCount: counters.length,
    });
    return counters;
  } catch (error) {
    logger.error("Error fetching all counters", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Gets all platform counters
 */
const getAllPlatformCounters = async (): Promise<DocumentWithId<Counter>[]> => {
  logger.debug("Getting all platform counters");

  try {
    const collectionPath = CollectionPaths.platformCounters();
    const counterRepo = new FirestoreRepository<Counter>(
      collectionPath,
      Counter,
    );
    const counters = await counterRepo.find();

    logger.debug("Retrieved all platform counters", {
      counterCount: counters.length,
    });
    return counters;
  } catch (error) {
    logger.error("Error fetching all platform counters", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Resets a platform counter (admin function - use with caution)
 */
const resetPlatformCounter = async (
  entityType: PlatformEntityType,
  newValue?: number,
): Promise<void> => {
  logger.debug("Resetting platform counter", {
    entityType,
    newValue: newValue ?? "default",
  });

  const resetValue = newValue ?? ENTITY_ID_RANGES[entityType].startId - 1;
  await upsertPlatformCounter(entityType, resetValue);

  logger.debug("Successfully reset platform counter", {
    entityType,
    resetValue,
  });
};

/**
 * Convenience function: Generate tenant ID and GUID
 */
const generateTenantId = async (): Promise<{
  tenantId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("tenants");
  return { tenantId: entityId, guid };
};

/**
 * Convenience function: Generate restaurant ID and GUID
 */
const generateRestaurantId = async (): Promise<{
  restaurantId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("restaurants"); // scoped-path-ok: counter entity key (stored doc id)
  return { restaurantId: entityId, guid };
};

/**
 * Convenience function: Generate item ID and GUID
 */
const generateItemId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ itemId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "items",
    tenantId,
    locationNumericId,
  );
  return { itemId: entityId, guid };
};

/**
 * Range-reservation convenience: reserve `count` contiguous item ids in a
 * single read-modify-write. Use in bulk imports instead of calling
 * generateItemId per row.
 */
const generateItemIds = async (
  tenantId: number,
  locationNumericId: number,
  count: number,
): Promise<{ itemId: number; guid: string }[]> => {
  const reserved = await generateNextIds("items", tenantId, locationNumericId, count);
  return reserved.map(({ entityId, guid }) => ({ itemId: entityId, guid }));
};

// RecipeOps (Phase 14-R) operational entities (recipe_items / inventory_items /
// modifier_ingredients) do NOT mint numeric counters: their stable identity is a
// randomUUID `guid` (= Firestore doc id), the Supabase cross-store join key.
// No numeric-id generators exist for them by design (ro-5 / mi-1).

/**
 * Convenience function: Generate employee ID and GUID
 */
const generateEmployeeId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ employeeId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "employees",
    tenantId,
    locationNumericId,
  );
  return { employeeId: entityId, guid };
};

/**
 * Range-reservation convenience: reserve `count` contiguous employee ids in a
 * single read-modify-write. Use in bulk imports instead of calling
 * generateEmployeeId per row.
 */
const generateEmployeeIds = async (
  tenantId: number,
  locationNumericId: number,
  count: number,
): Promise<{ employeeId: number; guid: string }[]> => {
  const reserved = await generateNextIds(
    "employees",
    tenantId,
    locationNumericId,
    count,
  );
  return reserved.map(({ entityId, guid }) => ({ employeeId: entityId, guid }));
};

/**
 * Convenience function: Generate shift ID and GUID
 */
const generateShiftId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ shiftId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "shifts",
    tenantId,
    locationNumericId,
  );
  return { shiftId: entityId, guid };
};

/**
 * Convenience function: Generate shift task ID and GUID
 */
const generateShiftTaskId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ shiftTaskId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "shift_tasks",
    tenantId,
    locationNumericId,
  );
  return { shiftTaskId: entityId, guid };
};

/**
 * Convenience function: Generate course ID and GUID
 */
const generateCourseId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ courseId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "courses",
    tenantId,
    locationNumericId,
  );
  return { courseId: entityId, guid };
};

/**
 * Convenience function: Generate tag ID and GUID
 */
const generateTagId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ tagId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "tags",
    tenantId,
    locationNumericId,
  );
  return { tagId: entityId, guid };
};

/**
 * Convenience function: Generate discount ID and GUID
 */
const generateDiscountId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ discountId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "discounts",
    tenantId,
    locationNumericId,
  );
  return { discountId: entityId, guid };
};

/**
 * Convenience function: Generate void reason ID and GUID
 */
const generateVoidReasonId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ voidReasonId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "void_reasons",
    tenantId,
    locationNumericId,
  );
  return { voidReasonId: entityId, guid };
};

/**
 * Convenience function: Generate modifier group ID and GUID
 */
const generateModifierGroupId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ modifierGroupId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "modifier_groups",
    tenantId,
    locationNumericId,
  );
  return { modifierGroupId: entityId, guid };
};

/**
 * Range-reservation convenience: reserve `count` contiguous modifier-group ids
 * in a single read-modify-write. Use in bulk imports instead of calling
 * generateModifierGroupId per row.
 */
const generateModifierGroupIds = async (
  tenantId: number,
  locationNumericId: number,
  count: number,
): Promise<{ modifierGroupId: number; guid: string }[]> => {
  const reserved = await generateNextIds(
    "modifier_groups",
    tenantId,
    locationNumericId,
    count,
  );
  return reserved.map(({ entityId, guid }) => ({
    modifierGroupId: entityId,
    guid,
  }));
};

/**
 * Convenience function: Generate modifier ID and GUID
 */
const generateModifierId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ modifierId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "modifiers",
    tenantId,
    locationNumericId,
  );
  return { modifierId: entityId, guid };
};

/**
 * Convenience function: Generate menu group ID and GUID
 */
const generateMenuGroupId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ menuGroupId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "menu_groups",
    tenantId,
    locationNumericId,
  );
  return { menuGroupId: entityId, guid };
};

/**
 * Range-reservation convenience: reserve `count` contiguous menu-group ids in a
 * single read-modify-write. Use in bulk imports instead of calling
 * generateMenuGroupId per row.
 */
const generateMenuGroupIds = async (
  tenantId: number,
  locationNumericId: number,
  count: number,
): Promise<{ menuGroupId: number; guid: string }[]> => {
  const reserved = await generateNextIds(
    "menu_groups",
    tenantId,
    locationNumericId,
    count,
  );
  return reserved.map(({ entityId, guid }) => ({ menuGroupId: entityId, guid }));
};

/**
 * Convenience function: Generate sales category ID and GUID
 */
const generateSalesCategoryId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ salesCategoryId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "sales_categories",
    tenantId,
    locationNumericId,
  );
  return { salesCategoryId: entityId, guid };
};

/**
 * Convenience function: Generate tax rate ID and GUID
 */
const generateTaxRateId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ taxRateId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "tax_rates",
    tenantId,
    locationNumericId,
  );
  return { taxRateId: entityId, guid };
};

/**
 * Convenience function: Generate prep station ID and GUID
 */
const generatePrepStationId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ prepStationId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "prep_stations",
    tenantId,
    locationNumericId,
  );
  return { prepStationId: entityId, guid };
};

/**
 * Convenience function: Generate printer ID and GUID
 */
const generatePrinterId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ printerId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "printers",
    tenantId,
    locationNumericId,
  );
  return { printerId: entityId, guid };
};

/**
 * Convenience function: Generate kitchen buzzer ID and GUID.
 *
 * Mirrors `generateKitchenBuzzerId` in `packages/core/src/services/counterService.ts`
 * — the ONLY allocator for both `kitchenBuzzerId` and `guid` (see
 * `packages/models/src/kitchen/KitchenBuzzer.ts`). `kitchenBuzzers.ts` calls
 * this ONCE and uses the returned `guid` as the Firestore document id.
 */
const generateKitchenBuzzerId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ kitchenBuzzerId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "kitchen_buzzers",
    tenantId,
    locationNumericId,
  );
  return { kitchenBuzzerId: entityId, guid };
};

/**
 * Convenience function: Generate job title ID and GUID
 */
const generateJobTitleId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ jobId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "job_titles",
    tenantId,
    locationNumericId,
  );
  return { jobId: entityId, guid };
};

/**
 * Convenience function: Generate job permission set ID and GUID
 */
const generateJobPermissionId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ jobPermissionId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "job_permissions",
    tenantId,
    locationNumericId,
  );
  return { jobPermissionId: entityId, guid };
};

/**
 * Convenience function: Generate department ID and GUID
 */
const generateDepartmentId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ departmentId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "departments",
    tenantId,
    locationNumericId,
  );
  return { departmentId: entityId, guid };
};

/**
 * Convenience function: Generate cart ID and GUID
 */
const generateCartId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ cartId: number; guid: string }> => {
  logger.debug("Generating cart ID", { tenantId, restaurantId: locationNumericId });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new CounterError(
        `Tenant not found with tenantId: ${tenantId}`,
        "TENANT_NOT_FOUND",
      );
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new CounterError(
        `Restaurant not found with restaurantId: ${locationNumericId}`,
        "RESTAURANT_NOT_FOUND",
      );
    }

    // Use restaurant subcollection for counter
    const counterCollectionPath = CollectionPaths.counters(
      tenantDocId,
      locationDocId,
    );
    const counterRepo = new FirestoreRepository<Counter>(
      counterCollectionPath,
      Counter,
    );

    // Get or create counter for carts in this restaurant
    const existingCounters = await counterRepo.find([
      { field: "entityType", operator: "==", value: "carts" },
    ]);

    let counter: DocumentWithId<Counter>;
    const entityConfig = ENTITY_ID_RANGES["carts"];

    if (existingCounters.length === 0) {
      // Create new counter starting from 1 for this restaurant
      const counterData: ICounterData = {
        entityType: "carts",
        nextId: entityConfig.startId, // Will generate ID 1, next will be 2
      };

      const validatedData = CreateCounterSchema.parse(counterData);
      const counterPayload = {
        ...validatedData,
        createdAt: FirestoreRepository.getServerTimestamp(),
        updatedAt: FirestoreRepository.getServerTimestamp(),
      };
      await counterRepo.createWithId("carts", counterPayload);
      const createdCounter = await counterRepo.getById("carts");
      if (!createdCounter) {
        throw new CounterError(
          "Failed to create carts counter",
          "CREATE_FAILED",
        );
      }
      counter = createdCounter;
    } else {
      counter = existingCounters[0];
    }

    const newId = counter.nextId;
    const guid = uuidv4();

    // Update counter for next use
    // Note: entityType is omitted because it cannot be changed after creation
    const updateData = {
      nextId: newId + 1,
    };

    const validatedUpdateData = UpdateCounterSchema.parse(updateData);
    await counterRepo.updateByQuery(
      [{ field: "entityType", operator: "==", value: "carts" }],
      {
        ...validatedUpdateData,
        updatedAt: FirestoreRepository.getServerTimestamp(),
      },
    );

    logger.info("Generated cart ID successfully", { cartId: newId, guid });
    return { cartId: newId, guid };
  } catch (error) {
    logger.error("Failed to generate cart ID", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Convenience function: Generate unified domain ID and GUID (platform-level)
 */
const generateDomainId = async (): Promise<{
  domainId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("domains");
  return { domainId: entityId, guid };
};

/**
 * Convenience function: Generate system role ID and GUID (platform-level)
 */
const generateSystemRoleId = async (): Promise<{
  systemRoleId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("system_roles");
  return { systemRoleId: entityId, guid };
};

/**
 * Convenience function: Generate allergy category ID and GUID (platform-level)
 */
const generateAllergyCategoryId = async (): Promise<{
  allergyCategoryId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("allergy_categories");
  return { allergyCategoryId: entityId, guid };
};

/**
 * Convenience function: Generate ingredient ID and GUID (platform-level)
 */
const generateIngredientId = async (): Promise<{
  ingredientId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("ingredients");
  return { ingredientId: entityId, guid };
};

/**
 * Convenience function: Generate permission group ID and GUID (platform-level)
 */
const generatePermissionGroupId = async (): Promise<{
  permissionGroupId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("permission_groups");
  return { permissionGroupId: entityId, guid };
};

/**
 * Convenience function: Generate permission sub-access ID and GUID (platform-level)
 */
const generatePermissionSubAccessId = async (): Promise<{
  permissionSubAccessId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("permission_sub_accesses");
  return { permissionSubAccessId: entityId, guid };
};

/**
 * Convenience function: Generate media ID and GUID
 * Media items are stored in a restaurant subcollection
 */
const generateMediaId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ mediaId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "media",
    tenantId,
    locationNumericId,
  );
  return { mediaId: entityId, guid };
};

/**
 * Convenience function: Generate review ID and GUID
 * Reviews are stored in a restaurant subcollection
 */
const generateReviewId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ reviewId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "reviews",
    tenantId,
    locationNumericId,
  );
  return { reviewId: entityId, guid };
};

/**
 * Convenience function: Generate table layout ID and GUID
 * Table layouts are stored in a restaurant subcollection
 */
const generateTableLayoutId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ layoutId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "table_layouts",
    tenantId,
    locationNumericId,
  );
  return { layoutId: entityId, guid };
};

/**
 * Convenience function: Generate table ID and GUID
 * Tables are stored within table layouts in a restaurant subcollection
 */
const generateTableId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ tableId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "tables",
    tenantId,
    locationNumericId,
  );
  return { tableId: entityId, guid };
};

/**
 * Convenience function: Generate revenue center ID and GUID
 * Revenue centers are stored in a restaurant subcollection
 */
const generateRevenueCenterId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ revenueCenterId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "revenue_centers",
    tenantId,
    locationNumericId,
  );
  return { revenueCenterId: entityId, guid };
};

/**
 * Convenience function: Generate table assignment ID and GUID
 * Table assignments are stored in a restaurant subcollection
 */
const generateTableAssignmentId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ assignmentId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "table_assignments",
    tenantId,
    locationNumericId,
  );
  return { assignmentId: entityId, guid };
};

/**
 * Convenience function: Generate table merge ID and GUID
 * Table merges are stored in a restaurant subcollection
 */
const generateTableMergeId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ mergeId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "table_merges",
    tenantId,
    locationNumericId,
  );
  return { mergeId: entityId, guid };
};

/**
 * Convenience function: Generate waitlist entry ID and GUID
 * Waitlist entries are stored in a restaurant subcollection
 */
const generateWaitlistEntryId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ waitlistId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "waitlist_entries",
    tenantId,
    locationNumericId,
  );
  return { waitlistId: entityId, guid };
};

/**
 * Convenience function: Generate seated customer ID and GUID
 * Seated customers are stored in a restaurant subcollection
 */
const generateSeatedCustomerId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ seatedId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "seated_customers",
    tenantId,
    locationNumericId,
  );
  return { seatedId: entityId, guid };
};

/**
 * Convenience function: Generate reservation ID and GUID
 * Reservations are stored in a restaurant subcollection
 * NOTE: This is a skeleton implementation for future use
 */
const generateReservationId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ reservationId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "reservations",
    tenantId,
    locationNumericId,
  );
  return { reservationId: entityId, guid };
};

/**
 * Convenience function: Generate device ID and GUID
 * Devices are stored in a restaurant subcollection
 */
const generateDeviceId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ deviceId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "devices",
    tenantId,
    locationNumericId,
  );
  return { deviceId: entityId, guid };
};

/**
 * Convenience function: Generate notification ID and GUID.
 * Notifications are restaurant-level, like every other counter entity — same
 * `{ notificationId, guid }` shape as devices/departments/etc. (platform
 * consistency: no bespoke id scheme). Mirrors core counterService exactly.
 */
const generateNotificationId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<{ notificationId: number; guid: string }> => {
  const { entityId, guid } = await generateNextId(
    "notifications",
    tenantId,
    locationNumericId,
  );
  return { notificationId: entityId, guid };
};

/**
 * Convenience function: Generate support session ID and GUID
 * Support sessions are platform-level (top-level collection)
 */
const generateSupportSessionId = async (): Promise<{
  sessionId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("support_sessions");
  return { sessionId: entityId, guid };
};

/**
 * Generates a new business type ID and GUID.
 * Business types are platform-level (top-level collection)
 */
const generateBusinessTypeId = async (): Promise<{
  businessTypeId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("business_types");
  return { businessTypeId: entityId, guid };
};

/**
 * Generates a new business sub-category ID and GUID.
 * Business sub-categories are platform-level (top-level collection)
 */
const generateBusinessSubCategoryId = async (): Promise<{
  businessSubCategoryId: number;
  guid: string;
}> => {
  const { entityId, guid } = await generateNextId("business_sub_categories");
  return { businessSubCategoryId: entityId, guid };
};

/**
 * Validates an ID generation request before processing
 */
const validateIdGenerationRequest = (
  entityType: EntityType,
  tenantId?: number,
  locationNumericId?: number,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate entity type using schema
  if (!validateEntityType(entityType)) {
    errors.push(`Invalid entity type: ${entityType}`);
  }

  // Check if platform vs restaurant entity requirements are met
  if (isPlatformEntity(entityType)) {
    if (tenantId !== undefined || locationNumericId !== undefined) {
      errors.push(
        `Platform-level entity ${entityType} should not have tenantId or restaurantId parameters`,
      );
    }
  } else {
    if (tenantId === undefined) {
      errors.push(
        `Restaurant-level entity ${entityType} requires tenantId parameter`,
      );
    }
    if (locationNumericId === undefined) {
      errors.push(
        `Restaurant-level entity ${entityType} requires restaurantId parameter`,
      );
    }
    if (
      tenantId !== undefined &&
      (tenantId < 1 || !Number.isInteger(tenantId))
    ) {
      errors.push(`tenantId must be a positive integer, got: ${tenantId}`);
    }
    if (
      locationNumericId !== undefined &&
      (locationNumericId < 1 || !Number.isInteger(locationNumericId))
    ) {
      errors.push(
        `restaurantId must be a positive integer, got: ${locationNumericId}`,
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * The single, exported service object for all counter-related operations
 */
export const counterService = {
  // Restaurant-level counter operations
  getCounterByEntityType,
  upsertCounter,
  getAllCounters,
  resetCounter,

  // Platform-level counter operations
  getPlatformCounterByEntityType,
  upsertPlatformCounter,
  getAllPlatformCounters,
  resetPlatformCounter,

  // Universal ID generation (handles both platform and restaurant-level)
  generateNextId,

  // Range reservation — reserve a contiguous block of ids in one
  // read-modify-write (bulk imports; restaurant-level entities only)
  generateNextIds,

  // Convenience functions — platform-level entities
  generateTenantId,
  generateRestaurantId,
  generateDomainId,
  generateSystemRoleId,
  generateAllergyCategoryId,
  generateIngredientId,
  generatePermissionGroupId,
  generatePermissionSubAccessId,
  generateSupportSessionId,
  generateBusinessTypeId,
  generateBusinessSubCategoryId,

  // Convenience functions — restaurant-level entities
  generateItemId,
  generateItemIds,
  generateEmployeeId,
  generateEmployeeIds,
  generateModifierGroupIds,
  generateMenuGroupIds,
  generateShiftId,
  generateShiftTaskId,
  generateDepartmentId,
  generateCourseId,
  generateTagId,
  generateModifierGroupId,
  generateModifierId,
  generateDiscountId,
  generateMenuGroupId,
  generateSalesCategoryId,
  generateTaxRateId,
  generatePrepStationId,
  generatePrinterId,
  generateKitchenBuzzerId,
  generateJobTitleId,
  generateJobPermissionId,
  generateCartId,
  generateMediaId,
  generateReviewId,
  generateTableLayoutId,
  generateTableId,
  generateRevenueCenterId,
  generateTableAssignmentId,
  generateTableMergeId,
  generateWaitlistEntryId,
  generateSeatedCustomerId,
  generateReservationId,
  generateDeviceId,
  generateNotificationId,
  generateVoidReasonId,

  // Utility functions
  validateIdRange,
  validateEntityType,
  validateCounterData,
  validateCounter,
  generateSuggestedPLU,
  createCounterModel,
  isPlatformEntity,
  validateIdGenerationRequest,
  counterToInterface,

  // Constants
  ENTITY_ID_RANGES,
};
